/**
 * FinanceAnalyticsBoard — Iter-156 · Phase 6a.
 * MRR / ARR / AR-aging + QuickBooks connection status. Consumes
 * /api/iter156/finance/dashboard and /api/iter156/quickbooks/status.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

const AGING_LABELS = { current: 'Current', '1_30': '1-30 days', '31_60': '31-60 days', '61_90': '61-90 days', over_90: '90+ days' };
const AGING_TONES = { current: '#10b981', '1_30': '#facc15', '31_60': '#f97316', '61_90': '#ef4444', over_90: '#111827' };

export default function FinanceAnalyticsBoard() {
  const [snap, setSnap] = useState(null);
  const [qb, setQb] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await fetch(`${API}/api/iter156/finance/dashboard`); if (r.ok) setSnap(await r.json()); } catch (_) {}
      try { const r2 = await fetch(`${API}/api/iter156/quickbooks/status`); if (r2.ok) setQb(await r2.json()); } catch (_) {}
    })();
  }, []);
  if (!snap) return <p className="text-zinc-500 text-sm" data-testid="finance-analytics-loading">Loading finance snapshot…</p>;
  return (
    <div className="space-y-5" data-testid="finance-analytics-board">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['MRR', `$${Number(snap.mrr).toLocaleString()}`, 'Monthly recurring'],
          ['ARR', `$${Number(snap.arr).toLocaleString()}`, 'Annualized'],
          ['Collected', `$${Number(snap.collected).toLocaleString()}`, 'YTD'],
          ['Outstanding', `$${Number(snap.outstanding).toLocaleString()}`, 'Uncollected'],
        ].map(([label, value, sub]) => (
          <Card key={label} className="p-4 bg-[#141414] border-zinc-800" data-testid={`finance-kpi-${label.toLowerCase()}`}>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400">{label}</p>
            <p className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</p>
            <p className="text-[10px] text-zinc-500 mt-1">{sub}</p>
          </Card>
        ))}
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: GOLD }}>AR AGING</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(snap.ar_aging).map(([k, v]) => (
            <div key={k} className="rounded-lg p-3 border" style={{ backgroundColor: '#141414', borderColor: AGING_TONES[k] + '55' }} data-testid={`ar-aging-${k}`}>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: AGING_TONES[k] }}>{AGING_LABELS[k]}</p>
              <p className="text-xl font-bold text-white mt-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>${Number(v).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      {qb && (
        <div className="rounded-lg border p-4 text-sm" style={{ backgroundColor: '#141414', borderColor: '#f9731655' }} data-testid="qbo-status-card">
          <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: GOLD }}>QuickBooks Online</p>
          <p className="text-white font-semibold" data-testid="qbo-connection-status">
            {qb.authorized ? 'Authorization stored · company access unverified' : qb.configured ? 'Configured · Intuit authorization pending' : 'QuickBooks not connected · setup required'}
          </p>
          <p className="text-xs text-zinc-300 mt-1" data-testid="qbo-connection-note">{qb.note}</p>
          <p className="text-xs text-amber-300 mt-2" data-testid="qbo-write-hold">Accounting writes are held pending company verification and an approved test.</p>
        </div>
      )}
    </div>
  );
}
